import { Immutable } from "immer";
import React from "react";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import {
    IFormUUID,
    IReviewsBrandID,
    WebPageURL,
    isoReviewsProductID,
    isoReviewsProductVariantID,
} from "../../../models/nominals";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import { getInteger } from "../../../utils/numbers";
import { WriteReviewForm } from "../WriteReviewForm";
import { WriteReviewThankYou } from "../WriteReviewThankYou";
import { defaultState } from "../defaults";
import { loadReviewTemplate } from "../thunks";

type IProps = Immutable<{
    formUUID: IFormUUID;
    brandID: IReviewsBrandID;
    brand?: string;
    returnURL?: WebPageURL;
}>;

export const SingleProductWriteReviewForm = (
    props: IProps,
): React.ReactNode => {
    const dispatch = useAppDispatch();

    const productTypeIDs = useAppSelector((rootState) => {
        const state = rootState.reviews || defaultState;
        return state.ui.productTypeIDs;
    });

    const facetValues = useAppSelector((rootState) => {
        const state = rootState.reviews || defaultState;
        return state.ui.facetValues;
    });

    // Client-side URL param handling for product pre-selection.
    // The CDN strips query params so SSR can't access them; email
    // links like ?product_id=390&product_variant_id=345 are handled
    // here after hydration.
    const [urlVariantID, setUrlVariantID] = React.useState(0);

    React.useEffect(() => {
        const w = getWindow();
        if (!w) return;
        const params = new URLSearchParams(w.location.search);

        const pidStr = params.get("product_id");
        if (pidStr) {
            const pid = parseInt(pidStr, 10);
            if (!isNaN(pid) && pid > 0) {
                void dispatch(
                    loadReviewTemplate({
                        formUUID: props.formUUID,
                        productID: isoReviewsProductID.wrap(pid),
                    }),
                );
            }
        }

        const vidStr = params.get("product_variant_id");
        if (vidStr) {
            const vid = parseInt(vidStr, 10);
            if (!isNaN(vid) && vid > 0) {
                setUrlVariantID(vid);
            }
        }
    }, [dispatch, props.formUUID]);

    const onReviewSubmit = () => {
        const document = getDocument();
        if (!document) {
            return;
        }
        const reviewModuleTitle = document.querySelector(
            ".write-review-button__container",
        );
        reviewModuleTitle?.scrollIntoView({ behavior: "smooth" });
    };

    const defaultProductTypeID = productTypeIDs[0];
    const defaultProductID = isoReviewsProductID.wrap(
        getInteger(facetValues.product_id, 0),
    );
    const defaultProductVariantID = isoReviewsProductVariantID.wrap(
        urlVariantID || getInteger(facetValues.product_variant_id, 0),
    );

    return (
        <div className="write-review-button__container">
            <WriteReviewThankYou formUUID={props.formUUID} />
            <WriteReviewForm
                formUUID={props.formUUID}
                formTitle={"Choose your product"}
                brand={props.brand}
                onReviewSubmit={onReviewSubmit}
                defaultProductTypeID={defaultProductTypeID}
                defaultProductID={defaultProductID}
                defaultProductVariantID={defaultProductVariantID}
                returnURL={props.returnURL}
            />
        </div>
    );
};
